<template>
  <div
    class="product-sub-navigation"
    :class="[
      `sticky-position-${stickyPosition}`,
      $class(scroll.navigation),
      {
        'product-sub-navigation--hide-tech-spec': !showTechSpecLink,
        'product-sub-navigation--is-tech-spec': pageType === 'tech-spec'
      }
    ]"
  >
    <div class="product-sub-navigation__container">
      <div class="product-sub-navigation__details">
        <span
          class="product-sub-navigation__title text-button-m"
          v-text="product.title || liquid?.productTitle"
        />

        <product-prices
          :available="product?.available"
          block-class="product-sub-navigation"
          :compare-at-price="product?.compareAtPrice?.amount"
          :price="displayPrice"
        />
      </div>

      <div class="product-sub-navigation__controls">
        <div class="product-sub-navigation__link">
          <component
            :is="pageType === 'pdp' ? 'span' : 'a'"
            class="text-button-m"
            :class="{ [$class('active')]: pageType === 'pdp' }"
            :href="productUrl"
            v-text="$string('product.sub_navigation.experience')"
          />
        </div>

        <div class="product-sub-navigation__link">
          <component
            :is="pageType === 'tech-spec' ? 'span' : 'a'"
            class="text-button-m"
            :href="techSpecUrl"
            :class="{ [$class('active')]: pageType === 'tech-spec' }"
            v-text="$string('product.sub_navigation.tech_specs')"
          />
        </div>

        <btn
          v-if="pageType === 'tech-spec'"
          block-class="product-sub-navigation__cta"
          modifiers="small dark"
          :label="$string('product.sub_navigation.buy')"
          :url="productUrl"
        />

        <btn
          v-else
          block-class="product-sub-navigation__cta"
          modifiers="small dark"
          :label="$string('product.sub_navigation.buy')"
          :show-icon="false"
          :show-state-icon="state === 'adding'"
          @click="handleBuyClick"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product sub navigation (product-sub-navigation)
 * -----------------------------------------------------------------------------
 * Sub navigation field on product page
 *
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Number} price - Price to display.
 * @param {String} pageType - Page type that the sub nav is on. Either 'pdp' or
 * 'tech-spec'
 * @param {Boolean} alwaysActive - Whether bar should always be active and
 * ignore scroll logic.
 */
import { mapState } from 'vuex'
import { scrollToElement } from '~/helpers/general'

import Btn from '~global/btn/btn'
import ProductPrices from '~global/product-prices/product-prices'

import IconLoading from '~icons/misc/loading.svg'

export default {
  name: 'ProductSubNavigation',

  components: {
    Btn,
    IconLoading,
    ProductPrices,
  },

  props: {
    stickyPosition: {
      type: String,
      default: 'top',
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    pageType: {
      type: String,
      default: 'pdp',
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        variantId: '',
      }),
    },
    alwaysActive: {
      type: Boolean,
      default: false,
    },
    state: {
      type: String,
      default: 'ready',
    },
  },

  emits: {
    'buy-clicked': () => true,
  },

  data() {
    return {
      nodeSelectors: false,
      scroll: {
        direction: 'none',
        navigation: this.navigationState(),
        isTriggered: false,
        x: 0,
        y: 0,
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      activeOverlays: (state) => state.overlays.active,
      settings: (state) => state.index.settings,
      headerState: (state) => state.index.scroll.header,
      scrollTop: (state) => state.index.scroll.y,
    }),

    /**
     * Compute product price.
     * - Before the product data has loaded, displays the liquid
     *   'variantPrice' prop.
     * - Otherwise displays the price prop.
     * - Prevents price flickering to 0/Free while the product is loading.
     */
    displayPrice() {
      if (!this.product) {
        return this.liquid.variantPrice
      }

      return this.price
    },

    /**
     * Compute product URL based on the props
     */
    productUrl() {
      if (!this.product) {
        return null
      }

      if (!this.variant) {
        return this.product.url
      }

      return `${this.product.url}?variant=${this.variant.id}`
    },

    /**
     * Compute product tech spec URL based on the props
     */
    techSpecUrl() {
      if (!this.productUrl) {
        return null
      }

      if (!this.variant) {
        return `${this.productUrl}?view=tech-spec`
      }

      return `${this.productUrl}&view=tech-spec`
    },

    /**
     * Compute whether to show the tech spec link.
     * - Link is hidden for spare parts products.
     */
    showTechSpecLink() {
      const isSparePart = this.product.tags?.includes('[customer_category_type:Sparepart]')

      return !isSparePart
    },
  },

  watch: {

    /**
     * Watch header state to update product sub-navigation class.
     */
    headerState() {
      if (this.alwaysActive) {
        return
      }

      if (this.headerState === 'open') {
        this.scroll.navigation = 'collapsed'
        document.body.classList.remove('is-active--subnav')
      }
    },

    /**
     * Watch scroll top value to update product sub-navigation class.
     */
    scrollTop() {
      if (this.alwaysActive) {
        return
      }

      if (
        this.activeOverlays.length ||
        !this.nodeSelectors.scrollTrigger
      ) {
        return
      }

      const triggerElement = this.nodeSelectors.scrollTrigger
      const triggerRect = triggerElement.getBoundingClientRect()
      const triggerBottom = triggerRect.bottom

      const isTriggered = triggerBottom < 0

      /**
       * If scroll difference is less than threshold don't update.
       */
      const difference = window.scrollY - this.scroll.y

      if (Math.abs(difference) < this.settings.threshold) {
        return
      }

      this.scroll = {
        direction: difference > 0 ? 'down' : 'up',
        isTriggered,
        x: window.scrollX,
        y: window.scrollY,
      }

      if (this.scroll.isTriggered) {
        this.scroll.navigation = 'open'
        document.body.classList.add('is-active--subnav')
      } else {
        this.scroll.navigation = 'collapsed'
        document.body.classList.remove('is-active--subnav')
      }
    },
  },

  mounted() {
    this.setNodeSelectors()

    if (this.alwaysActive) {
      document.body.classList.add('is-active--subnav')
    }
  },

  methods: {

    /**
     * Set node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        header: document.querySelector('[js-sign-post="site header"]'),
        scrollTrigger: document.querySelector('[js-sub-navigation="scrollTrigger"]'),
        productForm: {
          container: document.querySelector('[js-product-form="container"]'),
          button: document.querySelector('[js-product-form="add-to-cart"]'),
        },
      }
    },

    /**
     * Scroll to top of page.
     */
    scrollToPageTop() {
      if (this.pageType === 'tech-spec') {
        return
      }

      this.nodeSelectors.productForm.button.focus()

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    },

    /**
     * Scroll to product form button.
     */
    scrollToProductFormButton() {

      /**
       * If no product form button found, assume we are not on the PDP and
       * scroll to the top of the page instead.
       * - This is changed from previous behavior where we would add a url
       *   parameter to trigger scrolling on the PDP after navigation.
       * - See git history to see previous functionality.
       */
      if (!this.nodeSelectors?.productForm?.button) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })

        return
      }

      const productFormButton = this.nodeSelectors.productForm.button

      const header = this.nodeSelectors.header
      const productFormContainer = this.nodeSelectors.productForm.container

      const headerHeight = header ? header.getBoundingClientRect().height : 0
      const productFormHeight = productFormContainer
        ? productFormContainer.getBoundingClientRect().height
        : 0

      const scrollOffset = (headerHeight + productFormHeight) * -1

      scrollToElement(
        productFormButton,
        { callback: productFormButton.focus() },
        scrollOffset,
      )
    },

    /**
     * Determine navigation state based on page type.
     */
    navigationState() {
      if (this.pageType === 'tech-spec') {
        return 'sticky'
      }

      if (this.alwaysActive) {
        return 'open'
      }

      return 'collapsed'

    },

    /**
     * Handles the buy button click event.
     * - Emits a 'buy-clicked' event to notify the parent component.
     */
    handleBuyClick() {
      this.$emit('buy-clicked')
    },
  },
}
</script>

<style lang="scss">
@import './product-sub-navigation';
</style>
